import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { InterviewSlots } from '@components';

import { ANOTHER_ROUND_REASONS, InterviewSlotOption } from '@constants';
import {
  CandidateFeedbackFormik,
  DateTimeRange,
  ICandidateDetails,
} from '@types';
import { formatDate, interviewRoundsHeld } from '@utils';

export const ScheduleInterviewFields: React.FC<{
  candidateDetails: ICandidateDetails;
  formik: CandidateFeedbackFormik;
  isNextRound?: boolean;
}> = ({ candidateDetails, formik, isNextRound }) => {
  const latestCI = candidateDetails.interview?.at(-1) || null;
  const slots = latestCI?.candidateSlots || [];
  const firstName = candidateDetails.profile?.firstName || 'The candidate';
  const roundsHeld = interviewRoundsHeld(candidateDetails.interview);
  const submitted = formatDate(candidateDetails.submittedDate);

  const handleSlotChange = (newSlot: DateTimeRange | null) => {
    formik.setValues({
      ...formik.values,
      interviewSlots: newSlot ? [newSlot] : null,
      clientCalendar: null,
    });
  };

  const handleOptionChange = (option: InterviewSlotOption) => {
    formik.setValues({
      ...formik.values,
      interviewOption: option,
      interviewSlots: null,
      clientCalendar: null,
    });
  };

  const interviewSlotsError =
    ((formik.submitCount || formik.touched.interviewSlots) &&
      formik.errors.interviewSlots) ||
    '';

  const clientCalendarError =
    ((formik.submitCount || formik.touched.clientCalendar) &&
      formik.errors.clientCalendar) ||
    '';

  return (
    <Stack gap={1.5}>
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderRadius: 1,
          backgroundColor: 'text.linkLight',
        }}
      >
        <Typography variant="body1" fontWeight={500}>
          {isNextRound
            ? roundsHeld === 0
              ? 'No round has been held yet'
              : roundsHeld === 1
                ? 'One round has been held'
                : `${roundsHeld} rounds have been held`
            : submitted
              ? `Submitted ${submitted}, no interview booked yet`
              : 'No interview booked yet'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isNextRound
            ? `Asking for another round is not a rejection and decides nothing — ${firstName} stays open.`
            : `${firstName} has been waiting on this step. AgileEngine holds the slot with them once you send it.`}
        </Typography>
      </Box>

      {isNextRound && (
        <TextField
          select
          required
          fullWidth
          name="anotherRoundReason"
          label="What should this round settle?"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          value={formik.values.anotherRoundReason || ''}
          onChange={(event) =>
            formik.setFieldValue('anotherRoundReason', event.target.value)
          }
        >
          <MenuItem value="" disabled>
            Pick what this round is for
          </MenuItem>
          {ANOTHER_ROUND_REASONS.map((reason) => (
            <MenuItem key={reason} value={reason}>
              {reason}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Box>
        {Boolean(slots.length) && (
          <Typography variant="body2" color="text.secondary" mb={0.5}>
            {`Times AgileEngine has already checked with ${firstName}`}
          </Typography>
        )}
        <Box
          p={2}
          borderRadius={1}
          bgcolor="background.backgroundPrimary"
          minWidth={0}
        >
          <InterviewSlots
            slots={slots}
            selectedSlot={formik.values.interviewSlots?.[0] || null}
            clientCalendar={formik.values.clientCalendar}
            candidateReadyAnytime={latestCI?.candidateReadyAnytime}
            clientTimezone={formik.values.clientTimezone}
            interviewOption={formik.values.interviewOption}
            error={
              (typeof interviewSlotsError === 'string' &&
                interviewSlotsError) ||
              clientCalendarError
            }
            setInterviewOption={handleOptionChange}
            setSelectedSlot={handleSlotChange}
            setClientCalendar={(calendar) =>
              formik.setValues({
                ...formik.values,
                interviewSlots: null,
                clientCalendar: calendar,
              })
            }
            deleteSelectedSlot={() =>
              formik.setValues({
                ...formik.values,
                interviewSlots: null,
                interviewOption: null,
                clientCalendar: null,
              })
            }
          />
        </Box>
      </Box>
    </Stack>
  );
};

export const ScheduleInterviewNote: React.FC = () => (
  <Typography variant="body2" color="text.secondary">
    This sends a request. AgileEngine confirms the slot with the candidate
    before it is booked.
  </Typography>
);
